import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { X, FileText } from 'lucide-react';
import { FormModal, Field, FilePicker, inputCls, btnPrimary, btnGhost } from '../components/shared/FormKit';
import NowName from '../components/shared/NowName';
import { FormValidationProvider, useFormValidation } from '../components/shared/formValidation';
import ConfirmDeleteModal from '../components/shared/ConfirmDeleteModal';
import { RequestDetailShell, RequestHeader } from '../components/workflow/RequestDetailShell';
import StatusActionsCard from '../components/workflow/StatusActionsCard';
import ChangesSinceReturnCard from '../components/workflow/ChangesSinceReturnCard';
import WorkflowStatusBadge from '../components/workflow/WorkflowStatusBadge';
import { SectionCard, DetailGrid, DetailItem } from '../components/workflow/SectionCard';
import HistoryCard from '../components/workflow/HistoryCard';
import RejectModal from '../components/workflow/RejectModal';
import ReturnCommentsBanner from '../components/workflow/ReturnCommentsBanner';
import FieldCommentAnchor from '../components/workflow/FieldCommentAnchor';
import { useFieldComments, FieldCommentsProvider } from '../components/workflow/fieldComments';
import ApprovalsCard from '../components/workflow/ApprovalsCard';
import { EngagementName, EngagementNameList, AddedBadge, RemovedBadge, stripDupFyeLabel, fmtCoverageDefault } from '../components/workflow/CoverageDisplay';
import { formatDate, formatDateTime } from '../utils/dateFormat';
import { fetchJSON, API_BASE_URL } from '../utils/api';
import { ReviseSignOffField } from './WorkflowFsaNew';
import ReviewerSelect, { useClearOffRosterPick } from '../components/workflow/ReviewerSelect';
import { useFsAutoDetect } from '../components/workflow/FsDateAutoDetect';
import { FsAiConditionsList, FsAiPageThumbs } from '../components/workflow/FsAiReview';

const DOC_LABEL = { fs: 'Signed Financial Statements', qc35: 'QC35 Form' };

// 退件逐項意見用的欄位名。這條流程能改的就這三樣：修正後的簽核日、
// 修正理由、與更正文件本身（files 由 SHARED_CHANGE_LABELS 命名）。
const FSA_CHANGE_LABELS = {
  signingDate: 'Revised Signing Date',
  reason: 'Reason for the Amendment',
  reviewerName: 'Reviewer',
};

const WorkflowFsaDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [events, setEvents] = useState([]);
  const [error, setError] = useState(null);
  const [rejecting, setRejecting] = useState(false);
  const [modifying, setModifying] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [detail, hist] = await Promise.all([
        fetchJSON(`/api/workflow/fsa/${id}`),
        fetchJSON(`/api/workflow/fsa/${id}/events`),
      ]);
      setData(detail);
      setEvents(hist.data || []);
      setError(null);
    } catch (e) { setError(e.message); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const act = async (path, body) => {
    setBusy(true);
    setError(null);
    try {
      await fetchJSON(`/api/workflow/fsa/${id}/${path}`, {
        method: 'POST',
        body: JSON.stringify(body || {}),
      });
      await load();
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };

  const request = data?.request;
  const { currentEngagement, engagementAttachments, aiReview, review, canReview, canModify, canDelete, engagementLock } = data || {};
  const files = request?.files || [];
  // The engagement's current documents of the amended kind (for context).
  const currentDocs = request ? (engagementAttachments || []).filter((a) => {
    if (request.docType === 'fs') return (a.source === 'fs' && a.docType === 'fs') || (a.source === 'own' && a.docType === 'FS');
    return (a.source === 'fs' && a.docType === 'qc35') || (a.source === 'own' && a.docType === 'QC35');
  }) : [];
  // The OLD side of the old-vs-new comparison: while in review, the current
  // (non-superseded) document is the one being replaced; once approved the
  // corrected file took its place, so the old one is the superseded row.
  const oldDocs = request?.status === 'approved'
    ? currentDocs.filter((a) => a.superseded)
    : currentDocs.filter((a) => !a.superseded);

  // 退件的逐項意見：能退件的人就能逐格留言，被退回的人就讀得到。
  const canComment = !!request && (review?.canRejectOnly && request.status === 'requested');
  const fc = useFieldComments({
    events,
    canComment,
    returned: !!request && request.status === 'awaiting_info',
    labels: FSA_CHANGE_LABELS,
  });

  return (
    <RequestDetailShell
      backTo="/workflow/fsa"
      backLabel="Signed Financial Statements Amendment"
      loading={!data}
      error={error}
    >
      {request && (
        <FieldCommentsProvider value={fc}>
          <RequestHeader
            requestNo={request.requestNo}
            typeLabel="Signed Financial Statements Amendment"
            badge={<WorkflowStatusBadge status={request.status} />}
            chips={(
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-700">
                {DOC_LABEL[request.docType]}
              </span>
            )}
            submittedBy={request.requesterEmail}
            submittedAt={request.createdAt}
          />

          <StatusActionsCard
            lock={engagementLock}
            sides={[{
              key: 'main', label: null, status: request.status,
              note: request.statusUpdatedAt ? formatDateTime(request.statusUpdatedAt) : null,
            }]}
            busy={busy}
            events={events}
            onApprove={canReview && request.status === 'requested' ? () => act('approve') : null}
            onReject={canComment ? () => setRejecting(true) : null}
            onModify={canModify ? () => setModifying(true) : null}
            onResubmit={canModify && request.status === 'awaiting_info' ? () => act('resubmit') : null}
            onDelete={canDelete ? () => setDeleting(true) : null}
          />

          <ChangesSinceReturnCard
            events={events}
            changeLabels={FSA_CHANGE_LABELS}
            fmtChangeValue={fmtCoverageDefault}
          />

          <ApprovalsCard review={review} approvals={request.approvals || []} status={request.status} aiApproved={request.aiApproved} />

          {/* AI 自動核准的免審證據：編號清單判定＋當時送審的兩張頁面圖。 */}
          {request.aiApproved && aiReview && (
            <SectionCard title="AI Review" count={aiReview.passed ? 'Passed' : 'Not passed'}>
              <FsAiConditionsList conditions={aiReview.conditions} />
              <p className="mt-3 text-xs text-slate-500">
                Reviewed by {aiReview.model} on {formatDateTime(aiReview.createdAt)}.
              </p>
              <FsAiPageThumbs images={[
                aiReview.hasFirstPageImage && {
                  caption: 'Page 1 as reviewed',
                  url: `${API_BASE_URL}/api/workflow/fs/ai-reviews/${aiReview.id}/image/first`,
                },
                aiReview.hasPageImage && {
                  caption: 'Signing-date page as reviewed',
                  url: `${API_BASE_URL}/api/workflow/fs/ai-reviews/${aiReview.id}/image/page`,
                },
              ]} />
            </SectionCard>
          )}

          <SectionCard title="Amendment">
            <p className="text-sm text-slate-800"><EngagementName name={stripDupFyeLabel(request.engagementLabel)} serviceDescription={request.serviceDescription} /></p>
            {!currentEngagement && <p className="text-xs text-amber-600 mt-1">The engagement no longer exists.</p>}
            {((request.addEngagements || []).length > 0 || (request.removeEngagements || []).length > 0) && (
              <div className="mt-2 text-sm text-slate-700">
                <p className="text-xs text-slate-500 mb-0.5">Coverage Change Requested</p>
                <EngagementNameList
                  items={[
                    ...(request.addEngagements || []).map((e) => ({ ...e, delta: 'add' })),
                    ...(request.removeEngagements || []).map((e) => ({ ...e, delta: 'remove' })),
                  ]}
                  keyOf={(e, i) => `${e.delta}-${e.engagementId || i}`}
                  badgeOf={(e) => (e.delta === 'add' ? <AddedBadge /> : <RemovedBadge />)}
                />
              </div>
            )}
            <div className="mt-3">
              <DetailGrid>
                <DetailItem label="Document">{DOC_LABEL[request.docType]}</DetailItem>
                {request.docType === 'fs' && (
                  <DetailItem label="Revised Signing Date" field="signingDate">
                    {request.signingDate ? formatDate(request.signingDate) : 'Unchanged'}
                  </DetailItem>
                )}
                <DetailItem label="Current Engagement Sign-Off">
                  {currentEngagement?.signOffDate ? formatDate(currentEngagement.signOffDate) : '—'}
                </DetailItem>
                <DetailItem label="Reviewer" field="reviewerName">
                  {request.aiApproved
                    ? 'AI review — approved automatically'
                    : <NowName value={request.reviewerName} now={request.reviewerNameNow} />}
                </DetailItem>
              </DetailGrid>
            </div>
            <p className="text-xs text-slate-500 mt-3 break-words"><span className="font-medium text-slate-600">Reason:</span> {request.reason}</p>
            <FieldCommentAnchor field="reason" label="Reason for the Amendment" />
          </SectionCard>

          {/* Old vs new, side by side — the reviewer's core comparison. Left =
              the document being replaced (after approval: the superseded
              original), right = this amendment's corrected file; both open in
              a new tab. The old side downloads through this request's own
              engagement-files route — the named reviewer need not be an
              engagement member, so the member-scoped /api/my route cannot
              serve them. */}
          <SectionCard title="Corrected Document">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                <h3 className="text-xs font-semibold text-slate-500 uppercase mb-2">
                  {request.status === 'approved' ? 'Previous Document (Replaced)' : `Current ${DOC_LABEL[request.docType]} on File (Being Replaced)`}
                </h3>
                {oldDocs.length === 0 ? (
                  <p className="text-sm text-slate-500">—</p>
                ) : (
                  <ul className="space-y-1.5">
                    {oldDocs.map((a) => (
                      <li key={`${a.source}-${a.fileId}`} className="flex items-center gap-2 text-sm">
                        <FileText className="w-4 h-4 text-slate-400 shrink-0" />
                        <a
                          href={`${API_BASE_URL}/api/workflow/fsa/${request.id}/engagement-files/${a.source}/${a.fileId}`}
                          target="_blank"
                          rel="noreferrer"
                          className="truncate text-blue-600 hover:text-blue-800 underline"
                        >
                          {a.originalName}
                        </a>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="rounded-lg border border-blue-200 bg-blue-50/40 p-3">
                <h3 className="text-xs font-semibold text-blue-700 uppercase mb-2">
                  Corrected {DOC_LABEL[request.docType]} (This Amendment{request.status === 'approved' ? ' — Now Current' : ''})
                </h3>
                {files.length === 0 ? (
                  <p className="text-sm text-slate-500">No document attached.</p>
                ) : (
                  <ul className="space-y-1.5">
                    {files.map((f) => (
                      <li key={f.id} className="flex items-center gap-2 text-sm">
                        <FileText className="w-4 h-4 text-blue-400 shrink-0" />
                        <a
                          href={`${API_BASE_URL}/api/workflow/fsa/${request.id}/files/${f.id}`}
                          target="_blank"
                          rel="noreferrer"
                          className="truncate text-blue-600 hover:text-blue-800 underline"
                        >
                          {f.originalName}
                        </a>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
            <FieldCommentAnchor field="files" label="Attachments" />
          </SectionCard>

          <HistoryCard
            events={events}
            actionLabels={{ applied: ['Applied to engagement', 'bg-green-50 text-green-700'] }}
            changeLabels={FSA_CHANGE_LABELS}
            fmtChangeValue={fmtCoverageDefault}
          />

          {rejecting && (
            <RejectModal
              requestNo={request.requestNo}
              fieldComments={fc}
              onClose={() => setRejecting(false)}
              onSubmit={async (reason, fieldComments) => { await act('reject', { reason, fieldComments }); setRejecting(false); fc.clear(); }}
            />
          )}

          {modifying && (
            <ModifyModal
              request={request}
              events={events}
              currentSignOffDate={currentEngagement?.signOffDate}
              onClose={() => setModifying(false)}
              onDone={() => { setModifying(false); load(); }}
            />
          )}

          {deleting && (
            <ConfirmDeleteModal
              title={`Delete ${request.requestNo}`}
              message="Withdraw and delete this amendment request?"
              onClose={() => setDeleting(false)}
              onConfirm={async () => {
                await fetchJSON(`/api/workflow/fsa/${id}`, { method: 'DELETE' });
                navigate('/workflow/fsa');
              }}
            />
          )}
        </FieldCommentsProvider>
      )}
    </RequestDetailShell>
  );
};

// Modify — revise signing date (FS only), reason, and/or replace the document.
const ModifyModal = ({ request, events = [], currentSignOffDate = null, onClose, onDone }) => {
  const [signingDate, setSigningDate] = useState(request.signingDate || '');
  // 開關與 New 同一顆（ReviseSignOffField）——單子已經帶了修正日就預設打開。
  const [reviseSignOff, setReviseSignOff] = useState(!!request.signingDate);
  const [reviewerStaffId, setReviewerStaffId] = useState(
    request.reviewerStaffId ? String(request.reviewerStaffId) : ''
  );
  // Partner only — a reviewer who is not a serving Partner is cleared and must be re-picked.
  useClearOffRosterPick(reviewerStaffId, setReviewerStaffId, 'partner');
  const [reason, setReason] = useState(request.reason || '');
  const [file, setFile] = useState(null);
  // 涵蓋不在修改單上編輯（Signed Financial Statements 一間公司一筆）——改制前在途單存的
  // 涵蓋變更保持原樣，PATCH 不帶那兩個欄位就不會動到。
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  const existing = (request.files || [])[0];

  // Auto-detect works on a freshly picked replacement PDF only (the attached
  // file lives on the server; the scan is client-side). Same wrapping rule as
  // the New form: one array per selection, or the outcome resets every render.
  const detectFiles = useMemo(() => (file ? [file] : []), [file]);
  const detect = useFsAutoDetect({
    files: detectFiles,
    onDetected: (d) => { setReviseSignOff(true); setSigningDate(d); },
    disabled: saving,
  });

  // The revised date is required only while its switch is on; the reviewer
  // always. Marked on the controls after the first Save.
  const gaps = useMemo(() => [
    ...(request.docType === 'fs' && reviseSignOff && !signingDate
      ? [{ key: 'signingDate', label: 'Revised Sign-Off Date' }]
      : []),
    ...(reviewerStaffId ? [] : [{ key: 'reviewerName', label: 'Reviewer' }]),
  ], [request.docType, reviseSignOff, signingDate, reviewerStaffId]);
  const validation = useFormValidation(gaps);

  const save = async () => {
    if (!validation.validate()) { setError(null); return; }
    setError(null);
    setSaving(true);
    try {
      const body = new FormData();
      // 開關收起來＝清空修正日（空值＝維持現行簽核日）。
      if (request.docType === 'fs') body.set('signingDate', reviseSignOff ? (signingDate || '') : '');
      if (reason.trim()) body.set('reason', reason.trim());
      if (reviewerStaffId) body.set('reviewerStaffId', reviewerStaffId);
      if (file) body.set('file', file);
      await fetchJSON(`/api/workflow/fsa/${request.id}`, { method: 'PATCH', body });
      onDone();
    } catch (e) { setError(e.message); setSaving(false); }
  };

  return (
    <FormModal
      title={`Modify ${request.requestNo}`}
      size="lg"
      onClose={onClose}
      footer={(
        <>
          <button type="button" onClick={onClose} className={btnGhost}>Cancel</button>
          <button type="button" onClick={save} disabled={saving || detect.busy} className={btnPrimary}>{saving ? 'Saving…' : 'Save'}</button>
        </>
      )}
    >
      <FormValidationProvider value={validation.providerValue}>
        <ReturnCommentsBanner events={events} />
        {(error || validation.error) && (
          <div className="p-2.5 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700 text-sm">
            <span>{error || validation.error}</span>
            {error && <button type="button" onClick={() => setError(null)} className="ml-auto"><X className="w-3.5 h-3.5" /></button>}
          </div>
        )}
        <p className="text-sm text-slate-600"><EngagementName name={stripDupFyeLabel(request.engagementLabel)} serviceDescription={request.serviceDescription} /> — {DOC_LABEL[request.docType]}</p>
        <Field label="Replace Corrected Document" field="files" hint={`${existing ? `Uploading a new file replaces "${existing.originalName}".` : 'Upload the corrected document.'} Upload a PDF to enable signing-date auto-detection.`}>
          <FilePicker
            accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
            disabled={detect.busy}
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            files={file}
          />
          {!file && existing && <p className="text-xs text-slate-500 mt-1">Currently attached: {existing.originalName}</p>}
        </Field>
        {request.docType === 'fs' && (
          <ReviseSignOffField
            reviseSignOff={reviseSignOff}
            onToggle={setReviseSignOff}
            signingDate={signingDate}
            onSigningDateChange={setSigningDate}
            currentSignOffDate={currentSignOffDate}
            detect={detect}
          />
        )}
        <Field label="Reason" field="reason" required>
          <textarea rows="2" value={reason} onChange={(e) => setReason(e.target.value)} className={inputCls} />
        </Field>
        <Field label="Reviewer" field="reviewerName" required
          hint="You pick who reviews this amendment, from the firm's Partners. Changing it voids recorded approvals.">
          <ReviewerSelect value={reviewerStaffId} onChange={setReviewerStaffId} grade="partner" />
        </Field>
      </FormValidationProvider>
    </FormModal>
  );
};

export default WorkflowFsaDetail;
